/**
 * @module components/EditReferenceLine
 * @description 参考线配置（ChartSettingsV2）
 *
 * 状态管理模式：
 *   读/写: useSettingObjectState('reference_line')
 */

import React, { useCallback } from 'react';
import { Input } from 'tea-component';
import { useIntl, shallowEqual, useSelector } from 'umi';
import PlusButton from '@/pages/datalab/components/PlusButton';
import ColorSelector from '@/pages/datalab/components/ColorSelector';
import GridBox from '@/pages/datalab/components/GridBox';
import CustomJustify from '@/pages/datalab/components/CustomJustify';
import IconButtonWithBubble from '@/pages/datalab/components/IconButtonWithBubble';
import { dropPageBtnPoints } from '@/pages/datalab/utils/buiredDatalabBtnPoints/dropPageBtnPoint';
import ConfigCollapseCard from '@/pages/datalab/components/ConfigCollapseCard';
import LineTypeSelector from './LineTypeSelector';
import ValueSelector from './ValueSelector';
import { useSettingObjectState } from '../../context';
import {
  DEFAULT_REFERENCE_LINE_ITEM,
  type IReferenceLineItem,
  type IReferenceLineValue,
} from '../../types/settings/referenceLine';
import './index.scss';

export type { IReferenceLineItem };

interface IReferenceLineFormProps {
  count: number;
  data: IReferenceLineItem;
  onChange: (data: IReferenceLineItem) => void;
  onDelete: () => void;
}

const ReferenceLineForm: React.FC<IReferenceLineFormProps> = ({
  count,
  data,
  onChange,
  onDelete,
}) => {
  const { formatMessage } = useIntl();

  return (
    <div className="dbl-reference-line-form-card">
      <CustomJustify
        className="dbl-reference-line-form-card-header"
        left={formatMessage(
          { id: 'dbl_chart_advanced_reference_line_index' },
          { count },
        )}
        right={
          <IconButtonWithBubble
            bubbleConfig={{
              content: formatMessage({ id: 'dbl_common_delete' }),
              placement: 'top-end',
              arrowPointAtCenter: true,
              placementOffset: 15,
            }}
            iconConfig={{
              onClick: onDelete,
              type: 'close',
            }}
          />
        }
      />

      <GridBox
        grid={{
          row: 3,
          column: 2,
        }}
        columnSizes={[{ value: 'auto' }]}
        gap="20px"
      >
        <GridBox.Item type="label">
          {formatMessage({ id: 'dbl_chart_advanced_reference_line_style' })}
        </GridBox.Item>
        <GridBox.Item className="dbl-edit-reference-line-style">
          <LineTypeSelector
            value={data?.style?.type}
            onChange={(type: string) =>
              onChange({
                ...data,
                style: { color: data?.style?.color, type },
              })
            }
          />
          <ColorSelector
            value={data?.style?.color}
            onChange={(color: string) =>
              onChange({
                ...data,
                style: { type: data?.style?.type, color },
              })
            }
            defaultValue="#6A6D7E"
          />
        </GridBox.Item>

        <GridBox.Item type="label">
          {formatMessage({ id: 'dbl_chart_advanced_reference_line_value' })}
        </GridBox.Item>
        <GridBox.Item>
          <ValueSelector
            value={data?.value}
            onChange={(value: IReferenceLineValue) =>
              onChange({ ...data, value })
            }
          />
        </GridBox.Item>

        <GridBox.Item type="label">
          {formatMessage({ id: 'dbl_chart_advanced_reference_line_label' })}
        </GridBox.Item>
        <GridBox.Item>
          <Input
            style={{ width: '100%' }}
            value={data?.label}
            onChange={(label: string) => onChange({ ...data, label })}
            placeholder={formatMessage({
              id: 'dbl_chart_advanced_reference_line_label_placeholder',
            })}
          />
        </GridBox.Item>
      </GridBox>
    </div>
  );
};

const EditReferenceLine: React.FC = () => {
  const { formatMessage } = useIntl();
  const { value: lineGroups, setValue: setLineGroups } =
    useSettingObjectState('reference_line');

  const { currentGame } = useSelector(
    (state: Record<string, unknown>) => ({
      currentGame: (state as Record<string, Record<string, unknown>>)?.datalab
        ?.currentGame,
    }),
    shallowEqual,
  );

  const handleAddLine = useCallback(
    (event?: React.MouseEvent) => {
      event?.preventDefault();
      setLineGroups([...lineGroups, { ...DEFAULT_REFERENCE_LINE_ITEM }]);
      dropPageBtnPoints({
        buttonId: '003',
        buttonName: 'Add Reference Line',
        uid: (currentGame as { game_code?: string })?.game_code,
        gameName: (currentGame as { game_name?: string })?.game_name,
      });
    },
    [lineGroups, setLineGroups, currentGame],
  );

  return (
    <ConfigCollapseCard
      title={formatMessage({ id: 'dbl_chart_advanced_reference_line' })}
      className="dbl-edit-reference-line"
    >
      {lineGroups.map((item, index) => (
        <ReferenceLineForm
          key={index}
          count={index + 1}
          data={item}
          onChange={(newData) => {
            const newLineGroups = [...lineGroups];
            newLineGroups[index] = newData;
            setLineGroups(newLineGroups);
          }}
          onDelete={() => {
            setLineGroups(lineGroups.filter((_, i) => i !== index));
          }}
        />
      ))}

      <PlusButton
        className="dbl-reference-line-add"
        title={formatMessage({ id: 'dbl_chart_advanced_add_reference_line' })}
        onClick={handleAddLine}
      />
    </ConfigCollapseCard>
  );
};

export default React.memo(EditReferenceLine);
